export type TChatPresenceUser = {
  ulid: string
  name: string
}

export type TChatContact = {
  ulid: string
  name: string
  last_name: string
  avatar: string
  email: string
  online: boolean
}

export type TChatAttachment = {
  ulid: string
  original_name: string
  mime_type: string | null
  extension: string | null
  size_bytes: number
  kind: 'image' | 'file'
  url: string
  preview_url: string | null
}

/** Respuesta de POST /v1/chat/attachments */
export type TChatAttachmentUploadResponse = TChatAttachment & {
  upload_token: string
}

export type TChatMessage = {
  ulid: string
  body: string
  /** Vista previa para listados (solo archivos, etc.); alineado con backend */
  list_preview?: string
  attachments?: TChatAttachment[]
  chat_ulid?: string
  chat_type?: 'direct' | 'group'
  sender_ulid: string
  recipient_ulid?: string | null
  sender_name: string
  recipient_name?: string | null
  sender_avatar?: string | null
  /** Solo chats directos: avatar del otro participante */
  recipient_avatar?: string | null
  created_at: string
  updated_at: string
}

/** Estado local de un archivo en el composer antes de enviar el mensaje */
export type TAttachmentUploadDraft = {
  id: string
  file: File
  upload_token?: string
  progress: number
  status: 'queued' | 'uploading' | 'uploaded' | 'error'
  error?: string
  /** Object URL para miniatura de imagen local */
  previewUrl?: string
}

/** Vista previa de miembros para AvatarGroup en lista de conversaciones */
export type TChatGroupAvatarPreview = {
  ulid: string
  name: string
  avatar: string | null
}

/** Respuesta paginada estándar Laravel + JsonResource */
export type TLaravelPaginated<T> = {
  data: T[]
  links: {
    first: string | null
    last: string | null
    prev: string | null
    next: string | null
  }
  meta: {
    current_page: number
    from: number | null
    last_page: number
    path: string
    per_page: number
    to: number | null
    total: number
  }
}

export type TSendChatMessagePayload = {
  recipient_ulid?: string
  chat_ulid?: string
  body?: string
  attachment_tokens?: string[]
}

/** Texto de vista previa unificado (API + tiempo real). */
export const chatMessagePreviewLine = (m: TChatMessage): string => {
  const lp = (m.list_preview ?? '').trim()
  if (lp) return lp
  return (m.body ?? '').trim()
}

/** Payload del broadcast `.chat.user.typing` (Reverb/Echo). */
export type TChatUserTypingPayload = {
  peer_ulid: string
  peer_name: string
  typing: boolean
  chat_ulid?: string | null
}

export type TChatTypingPayload = {
  recipient_ulid?: string
  chat_ulid?: string
  typing: boolean
}

export type TChatConversation = {
  chat_ulid: string
  type: 'direct' | 'group'
  /** Nombre del peer (directo) o del grupo */
  name: string
  /** Directo: avatar del peer */
  avatar: string | null
  /** Grupo: hasta 3 miembros para AvatarGroup en lista */
  group_avatars?: TChatGroupAvatarPreview[] | null
  peer_ulid: string | null
  peer_name: string | null
  last_message_body: string
  last_message_at: string | null
  unread_count: number
  member_count: number
}

/** Miembro de grupo; auditoría de invitación/baja (API puede omitir en UI). */
export type TChatMember = {
  ulid: string
  name: string
  avatar: string | null
  removed_at: string | null
  added_by_ulid?: string | null
  added_by_name?: string | null
  removed_by_ulid?: string | null
  removed_by_name?: string | null
}

/** Foco de UI: conversación abierta */
export type TActiveConversationFocus = {
  /** Siempre que el hilo ya exista en backend */
  chat_ulid?: string
  type: 'direct' | 'group'
  /** Directo sin chat aún (primer mensaje): ulid del otro usuario */
  peer_ulid?: string
  display_name: string
}
